// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { materialsForYear } from '../data';
import { catInfo, FATES } from '../domain';
import { legendHtml } from '../components/charts';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatTonnes, formatPercent, fyShort } from '../utils/format';

type Mode = 'share' | 'tonnes';

export function renderTreatment(root: HTMLElement, ctx: ViewContext): void {
  const d = ctx.data;
  const y = d.latestYear;
  let mode: Mode = 'share';

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">What happens to each material</h1>
      <p class="view-sub">A cross-reference of every material against its ${glossaryTerm('fate', 'fate')}. Read across a row to see how a material
        is split between ${glossaryTerm('recycling', 'recycling')}, ${glossaryTerm('energy-recovery', 'energy recovery')}, ${glossaryTerm('reuse', 'reuse')} and
        ${glossaryTerm('disposal', 'landfill')}. Deeper colour = more of that material meets that fate.</p>
    </div>
    <div class="controls">
      <span class="field-label">Show</span>
      <div class="seg" role="group" data-role="mode">
        <button class="seg-btn" data-mode="share" aria-pressed="true">Row share (%)</button>
        <button class="seg-btn" data-mode="tonnes" aria-pressed="false">Tonnes</button>
      </div>
      <span class="field-label" style="margin-left:auto">${escapeHtml(fyShort(y))}</span>
    </div>
    <div class="panel">
      <div class="matrix-scroll" data-role="matrix"></div>
      <div style="margin-top:var(--space-md)" data-role="legend"></div>
    </div>
  `;

  const matrixEl = root.querySelector('[data-role="matrix"]') as HTMLElement;
  const legendEl = root.querySelector('[data-role="legend"]') as HTMLElement;

  function build(): void {
    const mats = materialsForYear(d, y);
    const head = `<thead><tr><th>Material</th>${FATES.map((f) => `<th>${escapeHtml(f.label)}</th>`).join('')}<th class="t-num">Total</th></tr></thead>`;
    const body = mats
      .map((m) => {
        const cells = FATES.map((f) => {
          const t = m.byFate.get(f.key) || 0;
          const share = m.total > 0 ? (t / m.total) * 100 : 0;
          const op = 0.12 + 0.78 * Math.min(1, share / 100);
          const shown = mode === 'share' ? formatPercent(share, 0) : (t > 0 ? formatTonnes(t) : '·');
          const txtColour = share > 45 ? '#fff' : 'var(--text-primary)';
          return `<td class="cell" style="background:${hexA(f.colour, t > 0 ? op : 0.05)};color:${txtColour}" data-tip="${escapeHtml(`${catInfo(m.key).short} → ${f.label}\n${formatTonnes(t)} (${formatPercent(share, 1)} of ${catInfo(m.key).short})`)}">${t > 0 ? shown : '·'}</td>`;
        }).join('');
        return `<tr><th data-open-material="${escapeHtml(m.key)}" role="button" style="cursor:pointer;color:${catInfo(m.key).colour}">${escapeHtml(catInfo(m.key).short)}</th>${cells}<td class="t-num" style="cursor:default">${formatTonnes(m.total)}</td></tr>`;
      })
      .join('');
    matrixEl.innerHTML = `<table class="matrix">${head}<tbody>${body}</tbody></table>`;
    matrixEl.querySelectorAll('[data-open-material]').forEach((el) => el.addEventListener('click', () => ctx.openMaterial(el.getAttribute('data-open-material') as string)));
    legendEl.innerHTML = legendHtml(FATES.map((f) => ({ colour: f.colour, label: f.label })));
    root.querySelectorAll('[data-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-mode') === mode)));
  }

  root.querySelector('[data-role="mode"]')?.addEventListener('click', (e) => { const b = (e.target as Element).closest('[data-mode]'); if (!b) return; mode = b.getAttribute('data-mode') as Mode; build(); });
  build();
}

/** Apply an alpha to a #rrggbb colour → rgba() string. */
function hexA(hex: string, a: number): string {
  const p = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${p[0]}, ${p[1]}, ${p[2]}, ${a.toFixed(2)})`;
}
